/** Booked clock times in the property's local zone. Matches Dispatch GO — never Morning/Afternoon ranges. */

const CANONICAL = {
  morning: { start: '09:00' },
  afternoon: { start: '12:00' },
  evening: { start: '16:00' },
};

const UNSET = '';
const DEFAULT_TZ = 'America/Toronto';

const IANA_TO_ABBREV = {
  'America/Vancouver': 'PT',
  'America/Whitehorse': 'MT',
  'America/Edmonton': 'MT',
  'America/Yellowknife': 'MT',
  'America/Winnipeg': 'CT',
  'America/Regina': 'CT',
  'America/Toronto': 'ET',
  'America/Montreal': 'ET',
  'America/Iqaluit': 'ET',
  'America/Halifax': 'AT',
  'America/Moncton': 'AT',
  'America/St_Johns': 'NT',
};

function jobTimezone(job) {
  const raw = (job?.timezone || job?.book_tz || '').trim();
  return raw || DEFAULT_TZ;
}

function zoneAbbrev(job) {
  const tz = jobTimezone(job);
  if (IANA_TO_ABBREV[tz]) return IANA_TO_ABBREV[tz];
  try {
    const posix = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value || '';
    const map = {
      PST: 'PT', PDT: 'PT', MST: 'MT', MDT: 'MT',
      CST: 'CT', CDT: 'CT', EST: 'ET', EDT: 'ET',
      AST: 'AT', ADT: 'AT', NST: 'NT', NDT: 'NT',
    };
    if (map[posix]) return map[posix];
    if (['PT', 'MT', 'CT', 'ET', 'AT', 'NT'].includes(posix)) return posix;
  } catch {
    /* invalid IANA — fall through */
  }
  return 'ET';
}

function clock(raw, tz) {
  const s = (raw || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('en-GB', {
      timeZone: tz || DEFAULT_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return '';
}

function clock12(hhmm) {
  if (!hhmm || hhmm[2] !== ':') return '';
  const hour = Number(hhmm.slice(0, 2));
  const minute = Number(hhmm.slice(3, 5));
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '';
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return '';
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/** confirmed_arrive_at, else time_window_start, else preferred_window start (legacy). */
function bookedClock(job) {
  const tz = jobTimezone(job);
  const confirmed = clock(job?.confirmed_arrive_at, tz);
  if (confirmed) return confirmed;
  const start = clock(job?.time_window_start, tz);
  if (start) return start;
  const key = (job?.preferred_window || '').trim().toLowerCase();
  if (CANONICAL[key]) return CANONICAL[key].start;
  return '';
}

/**
 * Booked arrive/start for OS listing file.
 * e.g. "2:00 PM PT" — never a window label or 12:00–16:00 range.
 */
export function formatShootWindow(job) {
  const hhmm = bookedClock(job);
  if (!hhmm) return UNSET;
  const pretty = clock12(hhmm);
  const abbrev = zoneAbbrev(job);
  return pretty ? `${pretty} ${abbrev}` : `${hhmm} ${abbrev}`;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatShootDate(scheduled_date) {
  const raw = (scheduled_date || '').slice(0, 10);
  if (!raw || raw[4] !== '-') return raw || '';
  const y = Number(raw.slice(0, 4));
  const m = Number(raw.slice(5, 7));
  const d = Number(raw.slice(8, 10));
  if (!y || !m || !d) return raw;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return raw;
  }
  return `${DOW[dt.getDay()]}, ${MON[m - 1]} ${d}`;
}

/** Date + booked clock: "Fri, Sep 25 · 2:00 PM PT". Empty string when nothing to show. */
export function formatShootWhen(job) {
  const date = formatShootDate(job?.scheduled_date);
  const window = formatShootWindow(job);
  if (!window) return date || '';
  if (date) return `${date} · ${window}`;
  return window;
}
